import React, { useCallback, useMemo, useState, useEffect } from "react";
import { bool, string } from "prop-types";
import DropdownItem from "./Item";
import DropdownBody from "./Body";
import DropdownTitle from "./Title";
import DropDownProvider, { DropDownConsumer } from "../utils/DropDownProvider";
import { useDropDown } from "../utils/DropDownProvider/context";
import useWindowSize from "../utils/useWindowSize";
import pureProps, { deepMergeProps } from "../utils/pureProps";

const dropdownProps = {
	action: null,
	sm: {
		action: null,
	},
	md: {
		action: null,
	},
	lg: {
		action: null,
	},
};

const ON_CLICK = "onClick";
const ON_HOVER = "onHover";
const ON_CSS = "onCSS";

const Dropdown = ({
	dropdown = { ...dropdownProps },
	className = "",
	isMegaMenu = false,
	isNotClosed = false,
	initialState,
	...props
}) => {
	const windowSize = useWindowSize();
	const [currentAction, setCurrentAction] = useState(null);

	const pureDropdownProps = deepMergeProps({ action: ON_CLICK }, pureProps(dropdown));

	const { action, sm, md, lg } = pureDropdownProps;

	const getActionName = useCallback(device => {
		const deviceActionName = device && typeof device.action === "string" ? device.action : "";
		return typeof device === "string" ? device : deviceActionName;
	}, []);

	const width = useMemo(() => +windowSize.width, [windowSize]);

	const isAll = useMemo(() => getActionName(action), [getActionName]);
	const isSM = useMemo(() => width < 640 && getActionName(sm), [width, getActionName]);
	const isMD = useMemo(() => width >= 640 && width < 1024 && getActionName(md), [
		width,
		getActionName,
	]);
	const isLG = useMemo(() => width >= 1024 && getActionName(lg), [width, getActionName]);

	const detectDevice = useCallback((actionName, { ...devices }) => {
		return (
			(devices.isAll === actionName && !devices.isSM && !devices.isMD && !devices.isLG) ||
			devices.isSM === actionName ||
			devices.isMD === actionName ||
			devices.isLG === actionName
		);
	}, []);

	const dataOnClick = useMemo(
		() => ({
			status: detectDevice(ON_CLICK, { isAll, isSM, isMD, isLG }),
			key: ON_CLICK,
		}),
		[detectDevice, isAll, isSM, isMD, isLG],
	);

	const dataOnHover = useMemo(
		() => ({
			status: detectDevice(ON_HOVER, { isAll, isSM, isMD, isLG }),
			key: ON_HOVER,
		}),
		[detectDevice, isAll, isSM, isMD, isLG],
	);

	const dataOnCSS = useMemo(
		() => ({
			status: detectDevice(ON_CSS, { isAll, isSM, isMD, isLG }),
			key: ON_CSS,
		}),
		[detectDevice, isAll, isSM, isMD, isLG],
	);
	const actionHandler = useCallback(
		fn => {
			let actionProps = {};
			if (dataOnHover.status) {
				actionProps = {
					onMouseEnter: fn,
					onMouseLeave: fn,
				};
			}
			if (dataOnClick.status) {
				actionProps = {
					onClick: fn,
				};
			}
			if (dataOnCSS.status) {
				actionProps = () => {};
			}
			return actionProps;
		},
		[dataOnHover, dataOnClick, dataOnCSS],
	);

	const initialAction = useMemo(() => {
		let result;
		switch (true) {
			case dataOnHover.status:
				result = dataOnHover.key;
				break;
			case dataOnCSS.status:
				result = dataOnCSS.key;
				break;
			case dataOnClick.status:
				result = dataOnClick.key;
				break;
			default:
				result = null;
				break;
		}
		return result;
	}, [dataOnHover, dataOnClick, dataOnCSS]);

	const handleChangeCurrentAction = useCallback(value => setCurrentAction(value), [
		initialAction,
		currentAction,
		setCurrentAction,
	]);

	useEffect(() => {
		setCurrentAction(initialAction);
	}, [setCurrentAction, initialAction]);

	return (
		<DropDownProvider
			initialAction={initialAction}
			currentAction={currentAction}
			changeCurrentAction={handleChangeCurrentAction}
			settings={pureDropdownProps}
			isMegaMenu={isMegaMenu}
			isNotClosed={isNotClosed}
			initialState={initialState}
		>
			<DropDownConsumer>
				{({ isOpen, toggle, containerRef }) => (
					<DropdownItem
						className={className}
						isOpen={isOpen}
						isMegaMenu={isMegaMenu}
						forwardRef={containerRef}
						dropdown={pureDropdownProps}
						{...(!isNotClosed ? actionHandler(toggle) : {})}
						{...props}
					/>
				)}
			</DropDownConsumer>
		</DropDownProvider>
	);
};

Dropdown.propTypes = {
	...DropdownItem.propTypes,
	isNotClosed: bool,
	className: string,
	initialState: bool,
};

const Body = props => {
	const { isOpen, settings } = useDropDown();

	return <DropdownBody settings={settings} isOpen={isOpen} {...props} />;
};

const Title = props => {
	const { isOpen } = useDropDown();
	return <DropdownTitle isOpen={isOpen} {...props} />;
};

Dropdown.Title = Title;
Dropdown.Body = Body;

export default Dropdown;
